import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { WagmiProvider } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import './styles.css';
import './api/index.js';  // side-effect: URL bootstrap (must run before AuthProvider mounts)
import { XPrim } from './primitives.jsx';
import { XShell } from './shell.jsx';
import { I18nProvider } from './i18n.jsx';
import './services.js';   // legacy XApi singleton — still consumed by pages not yet migrated
import { wagmiConfig } from './wagmi-config.js';
import './wallet.js';     // side-effect: registers window.XWallet bridge for services.js

const queryClient = new QueryClient();

import { PortalPage, MarketPage } from './pages/market.jsx';
import { ApiDetailPage } from './pages/api-detail.jsx';
import { ConsolePage } from './pages/console.jsx';
import { EarningsPage } from './pages/earnings.jsx';
import {
  MCPPage, WorkspacePage, AgentImportPage,
  LoginPage, TopUpPage, CapabilityPage,
} from './pages/other.jsx';
import { RegisterPage } from './pages/register.jsx';
import { CreateProjectPage } from './pages/create-project-modal.jsx';
import { WorkspaceApiPage } from './pages/workspace-api.jsx';
import { WorkspaceEndpointPage } from './pages/workspace-endpoint.jsx';
import { WorkspaceCallerPage } from './pages/workspace-caller.jsx';
import { SkillPage } from './pages/skill.jsx';
import {
  SkillCreatorPage,
  SkillDetailPage,
  SkillsMarketPage,
} from './pages/skills-market.jsx';
import { AIPage } from './pages/ai.jsx';
import { PlaygroundPage } from './pages/playground.jsx';
import { ModelsListPage, ModelDetailPage } from './pages/models.jsx';
import { BindPage } from './pages/bind.jsx';
import { ActivityPage } from './pages/activity.jsx';
import { DomainsPage } from './pages/domains.jsx';
import { AiSpecPage } from './pages/ai-spec.jsx';
import { EmailReminder } from './components/EmailReminder.jsx';
import { StripePaymentTrackingProvider } from './components/StripePaymentTracking.jsx';
import { adminConsoleUrl } from './admin-console.js';

const WorkersLegacyRedirect = () => {
  const { route, navigate } = XShell.useRoute();

  useEffect(() => {
    navigate('console', {
      tab: 'workers',
      ...(route.params.id ? { worker: route.params.id } : {}),
    });
  }, [navigate, route.params.id]);

  return (
    <main style={{ padding: 40, color: 'var(--fg-3)' }}>
      Opening Worker instances…
    </main>
  );
};

const AdminConsoleRedirect = ({ page, params }) => {
  useEffect(() => {
    window.location.replace(adminConsoleUrl(page, window.location.hostname, params));
  }, [page, params]);

  return (
    <main style={{ padding: 40, color: 'var(--fg-3)' }}>
      Opening the isolated Admin console…
    </main>
  );
};

const PageRouter = () => {
  const { route } = XShell.useRoute();
  switch (route.page) {
    case 'portal':           return <PortalPage/>;
    case 'market':           return <MarketPage/>;
    case 'api-detail':
      return route.params.asAdmin === 'true'
        ? <AdminConsoleRedirect page="api-detail" params={route.params}/>
        : <ApiDetailPage/>;
    case 'console':          return <ConsolePage/>;
    case 'earnings':         return <EarningsPage/>;
    case 'mcp':              return <MCPPage/>;
    case 'skill':            return <SkillPage/>;
    case 'skills':           return <SkillsMarketPage/>;
    case 'skill-detail':     return <SkillDetailPage/>;
    case 'skills-console':   return <SkillCreatorPage/>;
    case 'workers': return <WorkersLegacyRedirect />;
    case 'ai':               return <AIPage/>;
    // Legacy ?path=sandbox links also land on the unified Sandbox Playground.
    case 'sandbox':          return <PlaygroundPage/>;
    case 'playground':       return <PlaygroundPage/>;
    case 'models':           return <ModelsListPage/>;
    case 'model-detail':     return <ModelDetailPage/>;
    case 'ai-spec':          return <AiSpecPage/>;
    // Provider-only hub. Old 'my-apis' deep links still land here for backwards compat.
    case 'workspace':        return <WorkspacePage/>;
    case 'my-apis':          return <WorkspacePage/>;
    // Single-API provider workspace (Phase 1). 'workspace-new' for now alias to the
    // register flow's new-mode; once Phase 1 #2 splits register.jsx into components,
    // /workspace/new will mount BasicInfoForm + EndpointsManager directly.
    case 'workspace-api':    return <WorkspaceApiPage/>;
    case 'workspace-endpoint': return <WorkspaceEndpointPage/>;
    case 'workspace-caller':   return <WorkspaceCallerPage/>;
    case 'workspace-new':    return <CreateProjectPage/>;
    case 'register':         return <RegisterPage/>;
    case 'agent-import':     return <AgentImportPage/>;
    case 'admin':            return <AdminConsoleRedirect page={route.page}/>;
    case 'admin-ai-pending': return <AdminConsoleRedirect page={route.page}/>;
    case 'admin-ai-catalog': return <AdminConsoleRedirect page={route.page}/>;
    case 'login':            return <LoginPage/>;
    case 'register-account': return <LoginPage/>;
    case 'topup':            return <TopUpPage/>;
    case 'capability':       return <CapabilityPage/>;
    case 'bind':             return <BindPage/>;
    case 'activity':         return <ActivityPage/>;
    case 'domains':          return <DomainsPage/>;
    case 'docs':             return <DocsStub/>;
    default:                 return <PortalPage/>;
  }
};

const DocsStub = () => (
  <div style={{maxWidth: 760, margin:'0 auto', padding:'80px 32px', color:'var(--fg-2)'}}>
    <div className="micro">Documentation</div>
    <h1 style={{margin:'8px 0 16px', fontSize: 32, letterSpacing:'-0.02em', fontWeight: 500}}>Get started in 60 seconds</h1>
    <pre className="code">{`npm install @xapi/client
# or
pip install xapi`}</pre>
    <p style={{marginTop: 24, lineHeight: 1.8}}>Full API reference, SDK guides, and MCP integration docs are indexed at <span className="mono">docs.xapi.to</span>.</p>
  </div>
);

const App = () => {
  const [cmdOpen, setCmdOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setCmdOpen(v => !v); }
      if (e.key === 'Escape') { setCmdOpen(false); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    document.body.setAttribute('data-density', 'comfortable');
  }, []);

  return (
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <I18nProvider>
          <XPrim.ToastProvider>
            <XShell.AuthProvider>
              <XShell.RouteProvider>
                <StripePaymentTrackingProvider>
                  <XShell.TopBar onToggleCmd={() => setCmdOpen(v => !v)}/>
                  <PageRouter/>
                  <EmailReminder/>
                  <XShell.CmdPalette open={cmdOpen} onClose={() => setCmdOpen(false)}/>
                </StripePaymentTrackingProvider>
              </XShell.RouteProvider>
            </XShell.AuthProvider>
          </XPrim.ToastProvider>
        </I18nProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
};

ReactDOM.createRoot(document.getElementById('root')).render(<App/>);
